import React from "react";
import { useTypesDataContext } from "@/providers/TypesDataProvider";
import { MultiSelectDropdownWithField, MultiSelectOption } from "@/components/ui/dropdowns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/user-feedback/tooltip";

interface TypesSelectionElementProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  onBlur?: (value: string | string[]) => void;
  placeholder?: string;
  multiple?: boolean;
  filterOptions?: string[];
  disabled?: boolean;
}

export const TypesSelectionElement: React.FC<TypesSelectionElementProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = "Select types...",
  multiple = false,
  filterOptions = [],
  disabled = false,
}) => {
  // Convert string value to array for multiple mode, keep as-is for single mode
  const dropdownValue = multiple && typeof value === 'string' 
    ? (value ? value.split(',') : [])
    : value;

  // Handle onChange conversion: array -> string for multiple, keep as-is for single
  const handleDropdownChange = (newValue: string | string[]) => {
    if (multiple && Array.isArray(newValue)) {
      // Convert array to comma-separated string
      onChange(newValue.join(','));
    } else {
      // Single mode or already a string
      onChange(newValue);
    }
  };

  // Handle onBlur conversion
  const handleDropdownBlur = (newValue: string | string[]) => {
    if (onBlur) {
      if (multiple && Array.isArray(newValue)) {
        // Convert array to comma-separated string
        onBlur(newValue.join(','));
      } else {
        // Single mode or already a string
        onBlur(newValue);
      }
    }
  };

  const { types, isLoading } = useTypesDataContext();

  // Filter types based on filterOptions if filter is enabled
  const availableTypes = filterOptions.length > 0 
    ? types.filter(type => filterOptions.includes(type.id))
    : types;
  // Check if we should disable the dropdown
  const hasNoAvailableTypes = availableTypes.length === 0;
  const hasNoSelectableOptions = availableTypes.every(type => !type.data || type.data.length === 0);
  
  // Only disable if there are no available types or no selectable options
  // When there are no filter options, we should show all available types
  const shouldDisable = hasNoAvailableTypes || hasNoSelectableOptions;
  const finalDisabled = disabled || shouldDisable;

  // Convert types and their elements to MultiSelectOption format
  const typeOptions: MultiSelectOption[] = [];
  
  availableTypes.forEach(type => {
    // Add the main type as a non-selectable header
    typeOptions.push({
      id: `type-header-${type.id}`,
      label: type.name,
      description: type.description,
      disabled: true, // Make main type non-selectable
      isHeader: true // Mark as header for styling
    });
    
    // Add the type elements as selectable options
    if (type.data && type.data.length > 0) {
      type.data.forEach(element => {
        typeOptions.push({
          id: element.id,
          label: `${element.name}`, 
          description: element.description,
          parentTypeId: type.id, // Track which type this element belongs to
          parentTypeName: type.name // For display purposes
        });
      });
    } else {
      // If no elements, add a placeholder option
      typeOptions.push({
        id: `no-elements-${type.id}`,
        label: `No elements available`,
        description: `This type has no elements to select`,
        disabled: true,
        parentTypeId: type.id,
        parentTypeName: type.name
      });
    }
  });

  // Create the dropdown component
  const dropdownComponent = (
    <MultiSelectDropdownWithField
      value={dropdownValue}
      onChange={handleDropdownChange}
      onBlur={handleDropdownBlur}
      options={typeOptions}
      placeholder={placeholder}
      multiple={multiple}
      disabled={finalDisabled}
      isLoading={isLoading}
    />
  );

  // If we should disable due to no options, wrap with tooltip
  if (shouldDisable) {
    return (
      <TooltipProvider delayDuration={100} skipDelayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            {dropdownComponent}
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {hasNoAvailableTypes 
                ? "No types available. Please create some types first."
                : "No selectable options available in the current types."
              }
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Otherwise return the dropdown without tooltip
  return dropdownComponent;
};
